import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const resources = {
  en: {
    translation: {
      common: {
        appPreferences: 'App Preferences',
        language: 'Language',
        selectLanguage: 'Choose your preferred language',
        selected: 'Selected',
      },
      profile: {
        appPreferences: 'App Preferences',
        appPreferencesSubtitle: 'Choose your preferred language',
        helpCenter: 'Help Center',
        signOut: 'Sign Out',
        paymentInfo: 'Payment Info',
        paymentInfoTitle: 'Payment Information',
        paymentInfoSubtitle: 'Manage your payment methods for withdrawals',
        addDealer: 'Add Dealer',
        myDealer: 'My Dealer',
        privacySettings: 'Privacy Settings',
        // Payment form / view
        payment: {
          method: 'Payment Method',
          upiOption: 'UPI ID',
          bankOption: 'Bank Account',
          upiDetails: 'UPI Details',
          upiIdLabel: 'UPI ID',
          upiIdPlaceholder: 'Enter your UPI ID (e.g., name@bank)',
          validUpi: 'Please enter a valid UPI ID.',
          upiProofLabel: 'UPI Proof (QR/Screenshot)',
          upiProofUploaded: '(Uploaded)',
          bankDetails: 'Bank Details',
          accountHolder: 'Account Holder',
          bankName: 'Bank Name',
          accountNumber: 'Account Number',
          reEnterAccountNumber: 'Re-enter Account Number',
          ifsc: 'IFSC Code',
          bankProofLabel: 'Bank Proof (Passbook/Cheque/Statement)',
          bankProofUploaded: '(Uploaded)',
          warningProofRequired: 'Proof required to save payment details.',
          warningBankProof: 'Required: Upload bank proof (JPG/PNG only)',
          accountMismatch: 'Account numbers do not match',
          editCta: '✏️ Edit Payment Details',
          saveCta: 'Save Payment Details',
          submitting: 'Submitting...',
          submitCta: 'Submit for Review',
          cancelCta: 'Cancel',
          active: 'Active',
          saved: 'Saved',
          notSet: 'Not set',
          cooldownActive: 'Payment Change Cooldown Active',
          cooldownMessage: 'You can submit another payment change request after <strong>{{days}} day</strong>.',
          cooldownMessagePlural: 'You can submit another payment change request after <strong>{{days}} days</strong>.',
        },
        // Edit Profile form
        editProfile: {
          title: 'Show your profile',
          subtitle: 'View your profile information',
          firstName: 'First Name',
          lastName: 'Last Name',
          city: 'City',
          cityPlaceholder: 'Enter city',
          locality: 'Area / Locality',
          localityPlaceholder: 'Area / Locality',
          age: 'Age',
          agePlaceholder: 'Your age',
          pinCode: 'PIN Code',
          pinPlaceholder: '6-digit PIN',
          state: 'State *',
          district: 'District *',
          mobileNumber: 'Mobile Number',
          accountType: 'Account Type',
          selectState: 'Select State',
          selectDistrict: 'Select District',
          saveCta: 'SAVE & CONTINUE',
          saving: 'Saving...',
        },
        // KYC modal
        kyc: {
          title: 'KYC Details',
          realName: 'Real Name *',
          aadhaarNumber: 'Aadhaar Number *',
          aadhaarPhoto: 'Aadhaar Photo *',
          panNumber: 'PAN Number *',
          panPhoto: 'PAN Photo *',
          rejectedNote: 'Your previous KYC was rejected.',
          reasonPrefix: 'Reason:',
          mandatoryNote: 'Mandatory for withdrawal',
          submittedOn: 'Submitted on:',
          submitting: 'Submitting...',
          submitCta: 'Submit KYC',
          cancelCta: 'Cancel',
          closeCta: 'Close',
        },
      },
      transactions: {
        title: 'Transaction History',
        totalEarned: 'Total Earned',
        totalWithdrawn: 'Total Withdrawn',
        availableBalance: 'Available Balance',
        totalTransactions: 'Total Transactions',
        allTransactions: 'All transactions',
        filteredBy: 'Filtered by:',
        earnings: 'Earnings',
        withdrawals: 'Withdrawals',
        today: 'Today',
        yesterday: 'Yesterday',
        thisWeek: 'This Week',
        thisMonth: 'This Month',
        thisYear: 'Last 12 Months',
        allTime: 'All Time',
        customRange: 'Custom Range',
        filters: 'Filters',
        searchPlaceholder: 'Search transactions...',
        transactionType: 'Transaction Type',
        all: 'All',
        earned: 'Earned',
        withdrawn: 'Withdrawn',
        dateRange: 'Date Range',
        selectDateRange: 'Select Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        showingTransactions: 'Showing transactions from',
        to: 'to',
        status: 'Status',
        completed: 'Completed',
        pending: 'Pending',
        processing: 'Processing',
        paid: 'Paid',
        rejected: 'Rejected',
        failed: 'Failed',
        failedRejected: 'Failed/Rejected',
        cashback: 'Cashback',
        clearAllFilters: 'Clear All Filters',
        transactions: 'Transactions',
        noTransactionsFound: 'No transactions found',
        transactionHistoryWillAppear: 'Your transaction history will appear here',
        noTransactionsFoundFilter: 'No {{filter}} transactions found',
        paymentRequest: 'Payment Request: ₹{{amount}}',
        received: 'Received: ₹{{amount}}',
        couponRedemption: 'Coupon redemption',
        coupon: 'Coupon:',
      },
      scanner: {
        scanQrCode: 'Scan QR Code',
        alignQrCode: 'Align QR code within the frame',
        enableCameraPermission: 'Enable Camera Permission',
        cameraAccessRequired: 'Camera access is required to scan QR codes and earn cashback rewards',
        requestingPermission: 'Requesting Permission...',
        cantEnableCamera: 'Can\'t enable camera?',
        permissionDenied: 'Permission Denied - Manual Setup Required',
        manualPermissionInstructions: {
          chrome: 'Chrome/Edge: Click lock icon → Camera → Allow',
          safari: 'Safari: Safari → Settings → Websites → Camera',
          firefox: 'Firefox: Click shield icon → Permissions → Camera',
          refresh: 'Then refresh this page',
          goToSettings: 'Go to your browser settings',
          findPermissions: 'Find "Site permissions" or "Camera"',
          allowAccess: 'Allow camera access for this site',
          refreshPage: 'Refresh the page',
        },
        typeCodeManually: 'Type Code Manually',
        enterCouponCode: 'Enter Coupon Code',
        submitCode: 'Submit Code',
        processing: 'Processing...',
        validatingCouponCode: 'Validating coupon code...',
        couponValidated: 'Coupon validated',
        processingCompleted: 'Processing completed',
        validationFailed: 'Validation failed',
        userNotLoaded: 'User not loaded. Please try again.',
        failedToProcess: 'Failed to process coupon',
        scanSuccessful: 'Scan Successful',
        congratulations: 'Congratulations!',
        earnedCashback: 'You\'ve earned cashback',
        cashbackEarned: 'Cashback Earned',
        pointsEarned: 'Points Earned',
        point: 'POINT',
        yourPromoCode: 'Your promo code:',
        success: 'Success!',
        failed: 'Failed',
        scanAnother: 'Scan Another',
        tryAgain: 'Try Again',
        pts: 'pts',
        earned: 'earned!',
        total: 'Total:',
        points: 'Points:',
        processingWords: {
          connecting: 'Connecting',
          validating: 'Validating',
          checking: 'Checking',
          processing: 'Processing',
          calculating: 'Calculating',
          updating: 'Updating',
          verifying: 'Verifying',
          analyzing: 'Analyzing',
          confirming: 'Confirming',
        },
        processingObjects: {
          server: 'Server',
          coupon: 'Coupon',
          code: 'Code',
          cashback: 'Cashback',
          balance: 'Balance',
          wallet: 'Wallet',
          points: 'Points',
          transaction: 'Transaction',
          database: 'Database',
        },
      },
      payment: {
        confirmPaymentMethod: 'Confirm Payment Method',
        verifyPaymentDetails: 'Please verify your payment details',
        withdrawalAmount: 'Withdrawal Amount',
        youllReceive: 'You\'ll receive: ₹{{amount}} (after 5% fee)',
        choosePaymentMethod: 'Choose Payment Method:',
        paymentWillBeSentTo: 'Payment will be sent to:',
        bankAccount: 'Bank Account',
        bankName: 'Bank Name:',
        accountHolder: 'Account Holder:',
        accountNumber: 'Account Number:',
        ifscCode: 'IFSC Code:',
        notSet: 'Not set',
        upi: 'UPI',
        upiId: 'UPI ID:',
        cancel: 'Cancel',
        confirmSubmit: 'Confirm & Submit',
        paymentDetailsSecure: 'Your payment details are secure. Amount will be processed within 2-6 working days.',
        processingWithdrawal: 'Processing Withdrawal',
        pleaseWait: 'Please wait while we process your request...',
        validatingRequest: 'Validating request...',
        amountValidated: 'Amount validated',
        creatingPaymentRequest: 'Creating payment request...',
        paymentRequestCreated: 'Payment request created successfully',
        requestSubmitted: 'Request submitted!',
        done: 'Done ✅',
        withdrawalRequestSubmittedSuccess: 'Your withdrawal request has been submitted successfully!',
        availableBalance: 'Available Balance',
        rewardPoints: 'Reward Points',
        lastWithdrawal: 'Last Withdrawal',
        paymentCompleted: 'Payment Completed',
        processingPayment: 'Processing Payment',
        requestInProgress: 'Request in Progress',
        submitted: 'Submitted',
        underReview: 'Under Review',
        processingStatus: 'Processing',
        paidStatusLabel: 'Paid',
        pendingStatus: 'pending',
        paidStatus: 'Paid {{time}} • Amount transferred to your account',
        approvedStatus: 'Approved recently • Payment will be transferred soon',
        submittedStatus: 'Submitted {{time}} • Estimated: 2-6 working days',
        enterAmountToWithdraw: 'Enter amount to withdraw',
        enterAmount: 'Enter amount',
        minMax: 'Min: ₹100 | Max: ₹{{max}}',
        minimumWithdrawalAmount: 'Minimum withdrawal amount is ₹100',
        accountBanned: 'Your account is banned. Please contact support.',
        accountUnderReview: 'Your account is under review. Withdrawals are temporarily disabled.',
        pleaseEnterValidAmount: 'Please enter a valid amount',
        insufficientBalance: 'Insufficient balance. Available: ₹{{available}}',
        dailyLimitReached: 'Daily limit reached. Maximum 2 requests per day.',
        dailyAmountLimitExceeded: 'Daily withdrawal limit exceeded. Maximum ₹{{limit}} per day allowed. You have used ₹{{used}} today. You can withdraw up to ₹{{remaining}} more.',
        dailyAmountLimitReached: 'Daily withdrawal limit reached. Maximum ₹{{limit}} per day allowed.',
        withdrawalFailed: 'Withdrawal request failed. Please try again or contact support.',
        unauthenticated: 'Please log in again to continue.',
        invalidAmount: 'Invalid withdrawal amount. Please check and try again.',
        preconditionFailed: 'Request cannot be processed. Please check your balance and try again.',
        requestedAmount: 'Requested Amount:',
        serviceFee: 'Service Fee (5%):',
        youllReceiveLabel: 'You\'ll Receive:',
        requestWithdrawal: 'Request Withdrawal',
        processing: 'Processing...',
        lifetimeWithdrawn: 'Lifetime Withdrawn',
        thisMonth: 'This Month',
        totalRequests: 'Total Requests',
        yourRequestsSummary: 'Your Requests Summary',
        paid: 'Paid',
        pending: 'Pending',
        rejected: 'Rejected',
        fullHistory: 'Full History',
        updateBank: 'Update Bank',
        comingSoon: 'Coming Soon',
        rewardFeaturesComing: 'We\'re working on exciting reward features. Stay tuned!',
        goodToKnow: 'Good to Know',
        minimumPerWithdrawal: 'Minimum: ₹100 per withdrawal',
        processingTime: 'Processing: 2-6 working days',
        serviceFeeInfo: 'Service Fee: 5% (shown in breakdown)',
        dailyLimit: 'Daily Limit: Maximum 2 requests per day | Maximum ₹10,000 per day',
        paymentRequest: 'Payment Request',
        withdrawalRequestSubmitted: 'Your withdrawal request for ₹{{amount}} has been submitted for review',
        ago: 'ago',
        min: 'min',
        mins: 'mins',
        hour: 'hour',
        hours: 'hours',
        day: 'day',
        days: 'days',
      },
      dashboard: {
        totalEarnings: 'Total Earnings',
        loading: 'Loading...',
        availableBalance: 'Available Balance',
        memberSince: 'Member Since',
        bondhuId: 'BONDHU ID',
        activityOverview: 'Activity Overview',
        last3Months: 'Last 3 Months',
        last6Months: 'Last 6 Months',
        thisYear: 'Last 12 Months',
        noActivityData: 'No activity data available',
        earnings: 'Earnings',
        withdrawals: 'Withdrawals',
        whatsNew: 'What\'s New',
        live: 'LIVE',
        schemes: 'Schemes',
        loadingSchemes: 'Loading schemes...',
        noSchemesAvailable: 'No schemes available',
        downloads: 'downloads',
        quickLinks: 'Quick Links',
        termsAndConditions: 'Terms & Conditions',
        products: 'Products',
        exploreProducts: 'Explore our products',
        colourCombo: 'Colour Combo',
        colorCombinations: 'Color combinations',
        contact: 'Contact',
        getInTouch: 'Get in touch',
        customerSupport: 'Support',
        weAreHereToHelp: 'We\'re here to help!',
        hello: 'Hello! 👋',
        howCanWeHelp: 'How can we help you today?',
        featuresComingSoon: 'FEATURES COMING SOON',
        typeYourMessage: 'Type your message...',
        demoModeMessage: 'Demo Mode: Message sending will be implemented soon!\n\nYour message: ',
        poweredBy: 'Powered by LilQoder Ai',
        user: 'User',
        painter: 'Painter',
        fileNotAvailable: 'File not available yet. Please contact admin.',
        failedToDownload: 'Failed to download scheme',
      },
      profileHero: {
        rank: 'RANK',
        moreScansNeeded: '{{count}} more points needed • Progress to {{next}}',
        masterLevelAchieved: 'Master Level Achieved! • You have reached the highest level!',
        scans: 'scans',
        points: 'points',
        required: 'required',
        shareButton: 'Share',
        viewProfileButton: 'View Profile',
        level: {
          master: 'Master',
          diamond: 'Diamond',
          platinum: 'Platinum',
          gold: 'Gold',
          silver: 'Silver',
          bronze: 'Bronze',
          brightenUp: 'Brighten Up',
          locked: 'Locked',
        },
      },
    },
  },
  bn: {
    translation: {
      common: {
        appPreferences: 'অ্যাপ প্রেফারেন্স',
        language: 'ভাষা',
        selectLanguage: 'আপনার পছন্দের ভাষা বেছে নিন',
        selected: 'নির্বাচিত',
      },
      profile: {
        appPreferences: 'অ্যাপ প্রেফারেন্স',
        appPreferencesSubtitle: 'আপনার পছন্দের ভাষা বেছে নিন',
        helpCenter: 'হেল্প সেন্টার',
        signOut: 'সাইন আউট',
        paymentInfo: 'পেমেন্ট তথ্য',
        paymentInfoTitle: 'পেমেন্ট তথ্য',
        paymentInfoSubtitle: 'উইথড্রয়ের জন্য আপনার পেমেন্ট মাধ্যম ম্যানেজ করুন',
        addDealer: 'ডিলার যোগ করুন',
        myDealer: 'আমার ডিলার',
        privacySettings: 'গোপনীয়তা সেটিংস',
        payment: {
          method: 'পেমেন্ট পদ্ধতি',
          upiOption: 'ইউপিআই আইডি',
          bankOption: 'ব্যাংক অ্যাকাউন্ট',
          upiDetails: 'ইউপিআই তথ্য',
          upiIdLabel: 'ইউপিআই আইডি',
          upiIdPlaceholder: 'আপনার ইউপিআই আইডি লিখুন (যেমন: name@bank)',
          validUpi: 'সঠিক ইউপিআই আইডি লিখুন।',
          upiProofLabel: 'ইউপিআই প্রুফ (QR/স্ক্রিনশট)',
          upiProofUploaded: '(আপলোড করা হয়েছে)',
          bankDetails: 'ব্যাংক তথ্য',
          accountHolder: 'অ্যাকাউন্ট হোল্ডার',
          bankName: 'ব্যাংকের নাম',
          accountNumber: 'অ্যাকাউন্ট নম্বর',
          reEnterAccountNumber: 'অ্যাকাউন্ট নম্বর আবার লিখুন',
          ifsc: 'IFSC কোড',
          bankProofLabel: 'ব্যাংক প্রুফ (পাসবুক/চেক/স্টেটমেন্ট)',
          bankProofUploaded: '(আপলোড করা হয়েছে)',
          warningProofRequired: 'পেমেন্ট তথ্য সংরক্ষণে প্রুফ প্রয়োজন।',
          warningBankProof: 'প্রয়োজন: ব্যাংক প্রুফ আপলোড করুন (JPG/PNG মাত্র)',
          accountMismatch: 'অ্যাকাউন্ট নম্বর মেলেনি',
          editCta: '✏️ পেমেন্ট তথ্য সম্পাদনা করুন',
          saveCta: 'পেমেন্ট তথ্য সংরক্ষণ',
          submitting: 'জমা দিচ্ছে...',
          submitCta: 'রিভিউতে জমা দিন',
          cancelCta: 'বাতিল',
          active: 'সক্রিয়',
          saved: 'সংরক্ষিত',
          notSet: 'সেট করা হয়নি',
          cooldownActive: 'পেমেন্ট পরিবর্তন কুলডাউন সক্রিয়',
          cooldownMessage: 'আপনি <strong>{{days}} দিন</strong> পর আরেকটি পেমেন্ট পরিবর্তনের অনুরোধ জমা দিতে পারবেন।',
          cooldownMessagePlural: 'আপনি <strong>{{days}} দিন</strong> পর আরেকটি পেমেন্ট পরিবর্তনের অনুরোধ জমা দিতে পারবেন।',
        },
        editProfile: {
          title: 'আপনার প্রোফাইল দেখুন',
          subtitle: 'আপনার প্রোফাইল তথ্য দেখুন',
          firstName: 'প্রথম নাম',
          lastName: 'শেষ নাম',
          city: 'শহর',
          cityPlaceholder: 'শহর লিখুন',
          locality: 'এলাকা / লোকালিটি',
          localityPlaceholder: 'এলাকা / লোকালিটি',
          age: 'বয়স',
          agePlaceholder: 'আপনার বয়স',
          pinCode: 'পিন কোড',
          pinPlaceholder: '৬-সংখ্যার পিন',
          state: 'রাজ্য *',
          district: 'জেলা *',
          mobileNumber: 'মোবাইল নম্বর',
          accountType: 'অ্যাকাউন্ট ধরন',
          selectState: 'রাজ্য নির্বাচন করুন',
          selectDistrict: 'জেলা নির্বাচন করুন',
          saveCta: 'সেভ ও কন্টিনিউ',
          saving: 'সেভ হচ্ছে...',
        },
        kyc: {
          title: 'KYC বিবরণ',
          realName: 'আসল নাম *',
          aadhaarNumber: 'আধার নম্বর *',
          aadhaarPhoto: 'আধার ছবি *',
          panNumber: 'প্যান নম্বর *',
          panPhoto: 'প্যান ছবি *',
          rejectedNote: 'আপনার আগের KYC প্রত্যাখ্যাত হয়েছে।',
          reasonPrefix: 'কারণ:',
          mandatoryNote: 'উইথড্রয়ের জন্য বাধ্যতামূলক',
          submittedOn: 'জমা দেওয়া হয়েছে:',
          submitting: 'জমা দিচ্ছে...',
          submitCta: 'KYC জমা দিন',
          cancelCta: 'বাতিল',
          closeCta: 'বন্ধ করুন',
        },
      },
      transactions: {
        title: 'লেনদেনের ইতিহাস',
        totalEarned: 'মোট আয়',
        totalWithdrawn: 'মোট উত্তোলন',
        availableBalance: 'উপলব্ধ ব্যালেন্স',
        totalTransactions: 'মোট লেনদেন',
        allTransactions: 'সব লেনদেন',
        filteredBy: 'ফিল্টার করা হয়েছে:',
        earnings: 'আয়',
        withdrawals: 'উত্তোলন',
        today: 'আজ',
        yesterday: 'গতকাল',
        thisWeek: 'এই সপ্তাহ',
        thisMonth: 'এই মাস',
        thisYear: 'শেষ ১২ মাস',
        allTime: 'সব সময়',
        customRange: 'কাস্টম রেঞ্জ',
        filters: 'ফিল্টার',
        searchPlaceholder: 'লেনদেন খুঁজুন...',
        transactionType: 'লেনদেনের ধরন',
        all: 'সব',
        earned: 'আয়',
        withdrawn: 'উত্তোলন',
        dateRange: 'তারিখের রেঞ্জ',
        selectDateRange: 'তারিখের রেঞ্জ নির্বাচন করুন',
        startDate: 'শুরুর তারিখ',
        endDate: 'শেষ তারিখ',
        showingTransactions: 'লেনদেন দেখানো হচ্ছে',
        to: 'থেকে',
        status: 'স্ট্যাটাস',
        completed: 'সম্পন্ন',
        pending: 'অপেক্ষমান',
        processing: 'প্রক্রিয়াকরণ',
        paid: 'পেমেন্ট করা হয়েছে',
        rejected: 'প্রত্যাখ্যাত',
        failed: 'ব্যর্থ',
        failedRejected: 'ব্যর্থ/প্রত্যাখ্যাত',
        cashback: 'ক্যাশব্যাক',
        clearAllFilters: 'সব ফিল্টার সাফ করুন',
        transactions: 'লেনদেন',
        noTransactionsFound: 'কোনো লেনদেন পাওয়া যায়নি',
        transactionHistoryWillAppear: 'আপনার লেনদেনের ইতিহাস এখানে দেখাবে',
        noTransactionsFoundFilter: 'কোনো {{filter}} লেনদেন পাওয়া যায়নি',
        paymentRequest: 'পেমেন্ট রিকোয়েস্ট: ₹{{amount}}',
        received: 'প্রাপ্ত: ₹{{amount}}',
        couponRedemption: 'কুপন রিডেম্পশন',
        coupon: 'কুপন:',
      },
      scanner: {
        scanQrCode: 'QR কোড স্ক্যান করুন',
        alignQrCode: 'ফ্রেমের মধ্যে QR কোড সারিবদ্ধ করুন',
        enableCameraPermission: 'ক্যামেরা অনুমতি সক্রিয় করুন',
        cameraAccessRequired: 'QR কোড স্ক্যান এবং ক্যাশব্যাক রিওয়ার্ড অর্জনের জন্য ক্যামেরা অ্যাক্সেস প্রয়োজন',
        requestingPermission: 'অনুমতি অনুরোধ করা হচ্ছে...',
        cantEnableCamera: 'ক্যামেরা সক্রিয় করতে পারছেন না?',
        permissionDenied: 'অনুমতি প্রত্যাখ্যাত - ম্যানুয়াল সেটআপ প্রয়োজন',
        manualPermissionInstructions: {
          chrome: 'Chrome/Edge: লক আইকনে ক্লিক করুন → ক্যামেরা → অনুমতি দিন',
          safari: 'Safari: Safari → Settings → Websites → Camera',
          firefox: 'Firefox: শিল্ড আইকনে ক্লিক করুন → Permissions → Camera',
          refresh: 'তারপর এই পেজটি রিফ্রেশ করুন',
          goToSettings: 'আপনার ব্রাউজার সেটিংসে যান',
          findPermissions: '"Site permissions" বা "Camera" খুঁজুন',
          allowAccess: 'এই সাইটের জন্য ক্যামেরা অ্যাক্সেস অনুমতি দিন',
          refreshPage: 'পেজটি রিফ্রেশ করুন',
        },
        typeCodeManually: 'কোড ম্যানুয়ালি টাইপ করুন',
        enterCouponCode: 'কুপন কোড লিখুন',
        submitCode: 'কোড জমা দিন',
        processing: 'প্রক্রিয়াকরণ হচ্ছে...',
        validatingCouponCode: 'কুপন কোড যাচাই করা হচ্ছে...',
        couponValidated: 'কুপন যাচাই করা হয়েছে',
        processingCompleted: 'প্রক্রিয়াকরণ সম্পন্ন হয়েছে',
        validationFailed: 'যাচাইকরণ ব্যর্থ',
        userNotLoaded: 'ইউজার লোড হয়নি। আবার চেষ্টা করুন।',
        failedToProcess: 'কুপন প্রক্রিয়া করতে ব্যর্থ',
        scanSuccessful: 'স্ক্যান সফল',
        congratulations: 'অভিনন্দন!',
        earnedCashback: 'আপনি ক্যাশব্যাক অর্জন করেছেন',
        cashbackEarned: 'ক্যাশব্যাক অর্জিত',
        pointsEarned: 'পয়েন্ট অর্জিত',
        point: 'পয়েন্ট',
        yourPromoCode: 'আপনার প্রমো কোড:',
        success: 'সফল!',
        failed: 'ব্যর্থ',
        scanAnother: 'আরেকটি স্ক্যান করুন',
        tryAgain: 'আবার চেষ্টা করুন',
        pts: 'পয়েন্ট',
        earned: 'অর্জিত!',
        total: 'মোট:',
        points: 'পয়েন্ট:',
        processingWords: {
          connecting: 'সংযুক্ত হচ্ছে',
          validating: 'যাচাই করা হচ্ছে',
          checking: 'পরীক্ষা করা হচ্ছে',
          processing: 'প্রক্রিয়াকরণ',
          calculating: 'গণনা করা হচ্ছে',
          updating: 'আপডেট করা হচ্ছে',
          verifying: 'যাচাই করা হচ্ছে',
          analyzing: 'বিশ্লেষণ করা হচ্ছে',
          confirming: 'নিশ্চিত করা হচ্ছে',
        },
        processingObjects: {
          server: 'সার্ভার',
          coupon: 'কুপন',
          code: 'কোড',
          cashback: 'ক্যাশব্যাক',
          balance: 'ব্যালেন্স',
          wallet: 'ওয়ালেট',
          points: 'পয়েন্ট',
          transaction: 'লেনদেন',
          database: 'ডাটাবেস',
        },
      },
      payment: {
        confirmPaymentMethod: 'পেমেন্ট পদ্ধতি নিশ্চিত করুন',
        verifyPaymentDetails: 'অনুগ্রহ করে আপনার পেমেন্ট বিবরণ যাচাই করুন',
        withdrawalAmount: 'উত্তোলনের পরিমাণ',
        youllReceive: 'আপনি পাবেন: ₹{{amount}} (৫% ফি পরে)',
        choosePaymentMethod: 'পেমেন্ট পদ্ধতি বেছে নিন:',
        paymentWillBeSentTo: 'পেমেন্ট পাঠানো হবে:',
        bankAccount: 'ব্যাংক অ্যাকাউন্ট',
        bankName: 'ব্যাংকের নাম:',
        accountHolder: 'অ্যাকাউন্ট হোল্ডার:',
        accountNumber: 'অ্যাকাউন্ট নম্বর:',
        ifscCode: 'IFSC কোড:',
        notSet: 'সেট করা হয়নি',
        upi: 'UPI',
        upiId: 'UPI ID:',
        cancel: 'বাতিল',
        confirmSubmit: 'নিশ্চিত করুন এবং জমা দিন',
        paymentDetailsSecure: 'আপনার পেমেন্ট বিবরণ নিরাপদ। পরিমাণ ২-৬ কার্যদিবসের মধ্যে প্রক্রিয়াকরণ করা হবে।',
        processingWithdrawal: 'উত্তোলন প্রক্রিয়াকরণ',
        pleaseWait: 'অনুগ্রহ করে অপেক্ষা করুন, আমরা আপনার অনুরোধ প্রক্রিয়া করছি...',
        validatingRequest: 'অনুরোধ যাচাই করা হচ্ছে...',
        amountValidated: 'পরিমাণ যাচাই করা হয়েছে',
        creatingPaymentRequest: 'পেমেন্ট অনুরোধ তৈরি করা হচ্ছে...',
        paymentRequestCreated: 'পেমেন্ট অনুরোধ সফলভাবে তৈরি করা হয়েছে',
        requestSubmitted: 'অনুরোধ জমা দেওয়া হয়েছে!',
        done: 'সম্পন্ন ✅',
        withdrawalRequestSubmittedSuccess: 'আপনার উত্তোলন অনুরোধ সফলভাবে জমা দেওয়া হয়েছে!',
        availableBalance: 'উপলব্ধ ব্যালেন্স',
        rewardPoints: 'রিওয়ার্ড পয়েন্ট',
        lastWithdrawal: 'শেষ উত্তোলন',
        paymentCompleted: 'পেমেন্ট সম্পন্ন',
        processingPayment: 'পেমেন্ট প্রক্রিয়াকরণ',
        requestInProgress: 'অনুরোধ চলছে',
        submitted: 'জমা দেওয়া হয়েছে',
        underReview: 'পর্যালোচনাধীন',
        processingStatus: 'প্রক্রিয়াকরণ',
        paidStatusLabel: 'পেমেন্ট করা হয়েছে',
        pendingStatus: 'অপেক্ষমান',
        paidStatus: 'পেমেন্ট করা হয়েছে {{time}} • পরিমাণ আপনার অ্যাকাউন্টে স্থানান্তর করা হয়েছে',
        approvedStatus: 'সম্প্রতি অনুমোদিত • শীঘ্রই পেমেন্ট স্থানান্তর করা হবে',
        submittedStatus: 'জমা দেওয়া হয়েছে {{time}} • আনুমানিক: ২-৬ কার্যদিবস',
        enterAmountToWithdraw: 'উত্তোলনের পরিমাণ লিখুন',
        enterAmount: 'পরিমাণ লিখুন',
        minMax: 'সর্বনিম্ন: ₹১০০ | সর্বোচ্চ: ₹{{max}}',
        minimumWithdrawalAmount: 'সর্বনিম্ন উত্তোলনের পরিমাণ ₹১০০',
        accountBanned: 'আপনার অ্যাকাউন্ট নিষিদ্ধ। অনুগ্রহ করে সহায়তা নিন।',
        accountUnderReview: 'আপনার অ্যাকাউন্ট পর্যালোচনাধীন। উত্তোলন সাময়িকভাবে নিষ্ক্রিয়।',
        pleaseEnterValidAmount: 'অনুগ্রহ করে একটি বৈধ পরিমাণ লিখুন',
        insufficientBalance: 'অপর্যাপ্ত ব্যালেন্স। উপলব্ধ: ₹{{available}}',
        dailyLimitReached: 'দৈনিক সীমা পৌঁছেছে। দিনে সর্বোচ্চ ২টি অনুরোধ।',
        dailyAmountLimitExceeded: 'দৈনিক উত্তোলনের সীমা অতিক্রম করেছে। দিনে সর্বোচ্চ ₹{{limit}} অনুমোদিত। আপনি আজ ₹{{used}} ব্যবহার করেছেন। আপনি আরও ₹{{remaining}} উত্তোলন করতে পারবেন।',
        dailyAmountLimitReached: 'দৈনিক উত্তোলনের সীমা পৌঁছেছে। দিনে সর্বোচ্চ ₹{{limit}} অনুমোদিত।',
        withdrawalFailed: 'উত্তোলন অনুরোধ ব্যর্থ হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন বা সহায়তা নিন।',
        unauthenticated: 'চালিয়ে যেতে অনুগ্রহ করে আবার লগইন করুন।',
        invalidAmount: 'অবৈধ উত্তোলনের পরিমাণ। অনুগ্রহ করে পরীক্ষা করে আবার চেষ্টা করুন।',
        preconditionFailed: 'অনুরোধ প্রক্রিয়া করা যায়নি। অনুগ্রহ করে আপনার ব্যালেন্স পরীক্ষা করে আবার চেষ্টা করুন।',
        requestedAmount: 'অনুরোধকৃত পরিমাণ:',
        serviceFee: 'সার্ভিস ফি (৫%):',
        youllReceiveLabel: 'আপনি পাবেন:',
        requestWithdrawal: 'উত্তোলন অনুরোধ',
        processing: 'প্রক্রিয়াকরণ হচ্ছে...',
        lifetimeWithdrawn: 'সর্বকালীন উত্তোলন',
        thisMonth: 'এই মাস',
        totalRequests: 'মোট অনুরোধ',
        yourRequestsSummary: 'আপনার অনুরোধের সারাংশ',
        paid: 'পেমেন্ট করা হয়েছে',
        pending: 'অপেক্ষমান',
        rejected: 'প্রত্যাখ্যাত',
        fullHistory: 'সম্পূর্ণ ইতিহাস',
        updateBank: 'ব্যাংক আপডেট',
        comingSoon: 'শীঘ্রই আসছে',
        rewardFeaturesComing: 'আমরা উত্তেজনাপূর্ণ রিওয়ার্ড বৈশিষ্ট্য নিয়ে কাজ করছি। অপেক্ষা করুন!',
        goodToKnow: 'জানার মতো',
        minimumPerWithdrawal: 'সর্বনিম্ন: প্রতিটি উত্তোলনে ₹১০০',
        processingTime: 'প্রক্রিয়াকরণ: ২-৬ কার্যদিবস',
        serviceFeeInfo: 'সার্ভিস ফি: ৫% (বিবরণে দেখানো হয়েছে)',
        dailyLimit: 'দৈনিক সীমা: দিনে সর্বোচ্চ ২টি অনুরোধ | দিনে সর্বোচ্চ ₹১০,০০০',
        paymentRequest: 'পেমেন্ট অনুরোধ',
        withdrawalRequestSubmittedNotification: '₹{{amount}} এর জন্য আপনার উত্তোলন অনুরোধ পর্যালোচনার জন্য জমা দেওয়া হয়েছে',
        ago: 'আগে',
        min: 'মিনিট',
        mins: 'মিনিট',
        hour: 'ঘন্টা',
        hours: 'ঘন্টা',
        day: 'দিন',
        days: 'দিন',
      },
      dashboard: {
        totalEarnings: 'মোট আয়',
        loading: 'লোড হচ্ছে...',
        availableBalance: 'উপলব্ধ ব্যালেন্স',
        memberSince: 'সদস্য হওয়ার তারিখ',
        bondhuId: 'BONDHU ID',
        activityOverview: 'কার্যক্রমের ওভারভিউ',
        last3Months: 'শেষ ৩ মাস',
        last6Months: 'শেষ ৬ মাস',
        thisYear: 'শেষ ১২ মাস',
        noActivityData: 'কোনো কার্যক্রমের ডাটা পাওয়া যায়নি',
        earnings: 'আয়',
        withdrawals: 'উত্তোলন',
        whatsNew: 'নতুন কী আছে',
        live: 'লাইভ',
        schemes: 'স্কিম',
        loadingSchemes: 'স্কিম লোড হচ্ছে...',
        noSchemesAvailable: 'কোনো স্কিম পাওয়া যায়নি',
        downloads: 'ডাউনলোড',
        quickLinks: 'দ্রুত লিংক',
        termsAndConditions: 'শর্তাবলী',
        products: 'পণ্য',
        exploreProducts: 'আমাদের পণ্য দেখুন',
        colourCombo: 'রঙের কম্বো',
        colorCombinations: 'রঙের সংমিশ্রণ',
        contact: 'যোগাযোগ',
        getInTouch: 'যোগাযোগ করুন',
        customerSupport: 'সহায়তা',
        weAreHereToHelp: 'আমরা এখানে সাহায্যের জন্য!',
        hello: 'হ্যালো! 👋',
        howCanWeHelp: 'আজ আমরা আপনাকে কীভাবে সাহায্য করতে পারি?',
        featuresComingSoon: 'শীঘ্রই আসছে',
        typeYourMessage: 'আপনার বার্তা টাইপ করুন...',
        demoModeMessage: 'ডেমো মোড: বার্তা পাঠানো শীঘ্রই চালু হবে!\n\nআপনার বার্তা: ',
        poweredBy: 'LilQoder Ai দ্বারা চালিত',
        user: 'ইউজার',
        painter: 'পেইন্টার',
        fileNotAvailable: 'ফাইল এখনও পাওয়া যায়নি। অনুগ্রহ করে অ্যাডমিনের সাথে যোগাযোগ করুন।',
        failedToDownload: 'স্কিম ডাউনলোড করতে ব্যর্থ',
      },
      profileHero: {
        rank: 'র‍্যাঙ্ক',
        moreScansNeeded: 'আরও {{count}} পয়েন্ট প্রয়োজন • {{next}} এ অগ্রগতি',
        masterLevelAchieved: 'মাস্টার লেভেল অর্জিত! • আপনি সর্বোচ্চ স্তরে পৌঁছেছেন!',
        scans: 'স্ক্যান',
        points: 'পয়েন্ট',
        required: 'প্রয়োজন',
        shareButton: 'শেয়ার',
        viewProfileButton: 'প্রোফাইল দেখুন',
        level: {
          master: 'মাস্টার',
          diamond: 'ডায়মন্ড',
          platinum: 'প্লাটিনাম',
          gold: 'গোল্ড',
          silver: 'সিলভার',
          bronze: 'ব্রোঞ্জ',
          brightenUp: 'ব্রাইটেন আপ',
          locked: 'লক করা',
        },
      },
    },
  },
  hi: {
    translation: {
      common: {
        appPreferences: 'ऐप प्रेफ़रेंस',
        language: 'भाषा',
        selectLanguage: 'अपनी पसंदीदा भाषा चुनें',
        selected: 'चयनित',
      },
      profile: {
        appPreferences: 'ऐप प्रेफ़रेंस',
        appPreferencesSubtitle: 'अपनी पसंदीदा भाषा चुनें',
        helpCenter: 'हेल्प सेंटर',
        signOut: 'साइन आउट',
        paymentInfo: 'भुगतान जानकारी',
        paymentInfoTitle: 'भुगतान जानकारी',
        paymentInfoSubtitle: 'निकासी के लिए अपने भुगतान तरीके प्रबंधित करें',
        addDealer: 'डीलर जोड़ें',
        myDealer: 'मेरा डीलर',
        privacySettings: 'गोपनीयता सेटिंग्स',
        payment: {
          method: 'भुगतान विधि',
          upiOption: 'UPI आईडी',
          bankOption: 'बैंक खाता',
          upiDetails: 'UPI विवरण',
          upiIdLabel: 'UPI आईडी',
          upiIdPlaceholder: 'अपनी UPI आईडी दर्ज करें (जैसे: name@bank)',
          validUpi: 'कृपया मान्य UPI आईडी दर्ज करें।',
          upiProofLabel: 'UPI प्रूफ (QR/स्क्रीनशॉट)',
          upiProofUploaded: '(अपलोड किया गया)',
          bankDetails: 'बैंक विवरण',
          accountHolder: 'खाता धारक',
          bankName: 'बैंक का नाम',
          accountNumber: 'खाता संख्या',
          reEnterAccountNumber: 'खाता संख्या पुनः दर्ज करें',
          ifsc: 'IFSC कोड',
          bankProofLabel: 'बैंक प्रूफ (पासबुक/चेक/स्टेटमेंट)',
          bankProofUploaded: '(अपलोड किया गया)',
          warningProofRequired: 'भुगतान विवरण सहेजने के लिए प्रूफ आवश्यक है।',
          warningBankProof: 'आवश्यक: बैंक प्रूफ अपलोड करें (JPG/PNG मात्र)',
          accountMismatch: 'खाता संख्या मेल नहीं खा रही',
          editCta: '✏️ भुगतान विवरण संपादित करें',
          saveCta: 'भुगतान विवरण सहेजें',
          submitting: 'सबमिट हो रहा है...',
          submitCta: 'समीक्षा के लिए सबमिट करें',
          cancelCta: 'रद्द करें',
          active: 'सक्रिय',
          saved: 'सहेजा गया',
          notSet: 'सेट नहीं',
          cooldownActive: 'भुगतान परिवर्तन कूलडाउन सक्रिय',
          cooldownMessage: 'आप <strong>{{days}} दिन</strong> के बाद एक और भुगतान परिवर्तन अनुरोध जमा कर सकते हैं।',
          cooldownMessagePlural: 'आप <strong>{{days}} दिन</strong> के बाद एक और भुगतान परिवर्तन अनुरोध जमा कर सकते हैं।',
        },
        editProfile: {
          title: 'अपनी प्रोफ़ाइल दिखाएं',
          subtitle: 'अपनी प्रोफ़ाइल जानकारी देखें',
          firstName: 'पहला नाम',
          lastName: 'अंतिम नाम',
          city: 'शहर',
          cityPlaceholder: 'शहर दर्ज करें',
          locality: 'क्षेत्र / लोकैलिटी',
          localityPlaceholder: 'क्षेत्र / लोकैलिटी',
          age: 'आयु',
          agePlaceholder: 'आपकी आयु',
          pinCode: 'पिन कोड',
          pinPlaceholder: '6-अंकों का पिन',
          state: 'राज्य *',
          district: 'जिला *',
          mobileNumber: 'मोबाइल नंबर',
          accountType: 'खाते का प्रकार',
          selectState: 'राज्य चुनें',
          selectDistrict: 'जिला चुनें',
          saveCta: 'सेव और जारी रखें',
          saving: 'सेव हो रहा है...',
        },
        kyc: {
          title: 'KYC विवरण',
          realName: 'वास्तविक नाम *',
          aadhaarNumber: 'आधार संख्या *',
          aadhaarPhoto: 'आधार फोटो *',
          panNumber: 'PAN संख्या *',
          panPhoto: 'PAN फोटो *',
          rejectedNote: 'आपका पिछला KYC अस्वीकृत हुआ था।',
          reasonPrefix: 'कारण:',
          mandatoryNote: 'निकासी के लिए अनिवार्य',
          submittedOn: 'जमा किया:',
          submitting: 'सबमिट हो रहा है...',
          submitCta: 'KYC जमा करें',
          cancelCta: 'रद्द करें',
          closeCta: 'बंद करें',
        },
      },
      transactions: {
        title: 'लेनदेन इतिहास',
        totalEarned: 'कुल कमाई',
        totalWithdrawn: 'कुल निकासी',
        availableBalance: 'उपलब्ध बैलेंस',
        totalTransactions: 'कुल लेनदेन',
        allTransactions: 'सभी लेनदेन',
        filteredBy: 'फ़िल्टर किया गया:',
        earnings: 'कमाई',
        withdrawals: 'निकासी',
        today: 'आज',
        yesterday: 'कल',
        thisWeek: 'इस सप्ताह',
        thisMonth: 'इस महीने',
        thisYear: 'पिछले 12 महीने',
        allTime: 'सभी समय',
        customRange: 'कस्टम रेंज',
        filters: 'फ़िल्टर',
        searchPlaceholder: 'लेनदेन खोजें...',
        transactionType: 'लेनदेन प्रकार',
        all: 'सभी',
        earned: 'कमाया',
        withdrawn: 'निकाला',
        dateRange: 'तारीख रेंज',
        selectDateRange: 'तारीख रेंज चुनें',
        startDate: 'शुरुआती तारीख',
        endDate: 'अंतिम तारीख',
        showingTransactions: 'लेनदेन दिखाया जा रहा है',
        to: 'से',
        status: 'स्थिति',
        completed: 'पूर्ण',
        pending: 'लंबित',
        processing: 'प्रसंस्करण',
        paid: 'भुगतान किया गया',
        rejected: 'अस्वीकृत',
        failed: 'असफल',
        failedRejected: 'असफल/अस्वीकृत',
        cashback: 'कैशबैक',
        clearAllFilters: 'सभी फ़िल्टर साफ़ करें',
        transactions: 'लेनदेन',
        noTransactionsFound: 'कोई लेनदेन नहीं मिला',
        transactionHistoryWillAppear: 'आपका लेनदेन इतिहास यहाँ दिखाई देगा',
        noTransactionsFoundFilter: 'कोई {{filter}} लेनदेन नहीं मिला',
        paymentRequest: 'भुगतान अनुरोध: ₹{{amount}}',
        received: 'प्राप्त: ₹{{amount}}',
        couponRedemption: 'कूपन रिडेम्प्शन',
        coupon: 'कूपन:',
      },
      scanner: {
        scanQrCode: 'QR कोड स्कैन करें',
        alignQrCode: 'फ्रेम के भीतर QR कोड संरेखित करें',
        enableCameraPermission: 'कैमरा अनुमति सक्षम करें',
        cameraAccessRequired: 'QR कोड स्कैन करने और कैशबैक रिवार्ड अर्जित करने के लिए कैमरा एक्सेस आवश्यक है',
        requestingPermission: 'अनुमति का अनुरोध कर रहे हैं...',
        cantEnableCamera: 'कैमरा सक्षम नहीं कर सकते?',
        permissionDenied: 'अनुमति अस्वीकृत - मैन्युअल सेटअप आवश्यक',
        manualPermissionInstructions: {
          chrome: 'Chrome/Edge: लॉक आइकन पर क्लिक करें → कैमरा → अनुमति दें',
          safari: 'Safari: Safari → Settings → Websites → Camera',
          firefox: 'Firefox: शील्ड आइकन पर क्लिक करें → Permissions → Camera',
          refresh: 'फिर इस पेज को रीफ्रेश करें',
          goToSettings: 'अपनी ब्राउज़र सेटिंग्स पर जाएं',
          findPermissions: '"Site permissions" या "Camera" खोजें',
          allowAccess: 'इस साइट के लिए कैमरा एक्सेस की अनुमति दें',
          refreshPage: 'पेज रीफ्रेश करें',
        },
        typeCodeManually: 'कोड मैन्युअल रूप से टाइप करें',
        enterCouponCode: 'कूपन कोड दर्ज करें',
        submitCode: 'कोड सबमिट करें',
        processing: 'प्रसंस्करण हो रहा है...',
        validatingCouponCode: 'कूपन कोड सत्यापित किया जा रहा है...',
        couponValidated: 'कूपन सत्यापित',
        processingCompleted: 'प्रसंस्करण पूर्ण',
        validationFailed: 'सत्यापन विफल',
        userNotLoaded: 'उपयोगकर्ता लोड नहीं हुआ। कृपया पुनः प्रयास करें।',
        failedToProcess: 'कूपन प्रसंस्करण में विफल',
        scanSuccessful: 'स्कैन सफल',
        congratulations: 'बधाई हो!',
        earnedCashback: 'आपने कैशबैक अर्जित किया है',
        cashbackEarned: 'कैशबैक अर्जित',
        pointsEarned: 'पॉइंट्स अर्जित',
        point: 'पॉइंट',
        yourPromoCode: 'आपका प्रोमो कोड:',
        success: 'सफल!',
        failed: 'विफल',
        scanAnother: 'एक और स्कैन करें',
        tryAgain: 'पुनः प्रयास करें',
        pts: 'पॉइंट्स',
        earned: 'अर्जित!',
        total: 'कुल:',
        points: 'पॉइंट्स:',
        processingWords: {
          connecting: 'कनेक्ट हो रहा है',
          validating: 'सत्यापित कर रहा है',
          checking: 'जांच कर रहा है',
          processing: 'प्रसंस्करण',
          calculating: 'गणना कर रहा है',
          updating: 'अपडेट कर रहा है',
          verifying: 'सत्यापित कर रहा है',
          analyzing: 'विश्लेषण कर रहा है',
          confirming: 'पुष्टि कर रहा है',
        },
        processingObjects: {
          server: 'सर्वर',
          coupon: 'कूपन',
          code: 'कोड',
          cashback: 'कैशबैक',
          balance: 'बैलेंस',
          wallet: 'वॉलेट',
          points: 'पॉइंट्स',
          transaction: 'लेनदेन',
          database: 'डेटाबेस',
        },
      },
      payment: {
        confirmPaymentMethod: 'भुगतान विधि की पुष्टि करें',
        verifyPaymentDetails: 'कृपया अपने भुगतान विवरण सत्यापित करें',
        withdrawalAmount: 'निकासी राशि',
        youllReceive: 'आपको मिलेगा: ₹{{amount}} (5% शुल्क के बाद)',
        choosePaymentMethod: 'भुगतान विधि चुनें:',
        paymentWillBeSentTo: 'भुगतान भेजा जाएगा:',
        bankAccount: 'बैंक खाता',
        bankName: 'बैंक का नाम:',
        accountHolder: 'खाता धारक:',
        accountNumber: 'खाता संख्या:',
        ifscCode: 'IFSC कोड:',
        notSet: 'सेट नहीं',
        upi: 'UPI',
        upiId: 'UPI ID:',
        cancel: 'रद्द करें',
        confirmSubmit: 'पुष्टि करें और सबमिट करें',
        paymentDetailsSecure: 'आपके भुगतान विवरण सुरक्षित हैं। राशि 2-6 कार्य दिवसों के भीतर प्रसंस्कृत की जाएगी।',
        processingWithdrawal: 'निकासी प्रसंस्करण',
        pleaseWait: 'कृपया प्रतीक्षा करें, हम आपका अनुरोध प्रसंस्कृत कर रहे हैं...',
        validatingRequest: 'अनुरोध सत्यापित किया जा रहा है...',
        amountValidated: 'राशि सत्यापित',
        creatingPaymentRequest: 'भुगतान अनुरोध बनाया जा रहा है...',
        paymentRequestCreated: 'भुगतान अनुरोध सफलतापूर्वक बनाया गया',
        requestSubmitted: 'अनुरोध सबमिट किया गया!',
        done: 'पूर्ण ✅',
        withdrawalRequestSubmittedSuccess: 'आपका निकासी अनुरोध सफलतापूर्वक सबमिट किया गया है!',
        availableBalance: 'उपलब्ध बैलेंस',
        rewardPoints: 'रिवार्ड पॉइंट्स',
        lastWithdrawal: 'अंतिम निकासी',
        paymentCompleted: 'भुगतान पूर्ण',
        processingPayment: 'भुगतान प्रसंस्करण',
        requestInProgress: 'अनुरोध प्रगति में',
        submitted: 'सबमिट किया गया',
        underReview: 'समीक्षा के अधीन',
        processingStatus: 'प्रसंस्करण',
        paidStatusLabel: 'भुगतान किया गया',
        pendingStatus: 'लंबित',
        paidStatus: 'भुगतान किया गया {{time}} • राशि आपके खाते में स्थानांतरित की गई',
        approvedStatus: 'हाल ही में अनुमोदित • भुगतान जल्द ही स्थानांतरित किया जाएगा',
        submittedStatus: 'सबमिट किया गया {{time}} • अनुमानित: 2-6 कार्य दिवस',
        enterAmountToWithdraw: 'निकासी राशि दर्ज करें',
        enterAmount: 'राशि दर्ज करें',
        minMax: 'न्यूनतम: ₹100 | अधिकतम: ₹{{max}}',
        minimumWithdrawalAmount: 'न्यूनतम निकासी राशि ₹100 है',
        accountBanned: 'आपका खाता प्रतिबंधित है। कृपया सहायता से संपर्क करें।',
        accountUnderReview: 'आपका खाता समीक्षा के अधीन है। निकासी अस्थायी रूप से अक्षम है।',
        pleaseEnterValidAmount: 'कृपया एक वैध राशि दर्ज करें',
        insufficientBalance: 'अपर्याप्त बैलेंस। उपलब्ध: ₹{{available}}',
        dailyLimitReached: 'दैनिक सीमा पहुंच गई। प्रति दिन अधिकतम 2 अनुरोध।',
        dailyAmountLimitExceeded: 'दैनिक निकासी सीमा पार हो गई। प्रति दिन अधिकतम ₹{{limit}} अनुमत है। आपने आज ₹{{used}} का उपयोग किया है। आप ₹{{remaining}} और निकाल सकते हैं।',
        dailyAmountLimitReached: 'दैनिक निकासी सीमा पहुंच गई। प्रति दिन अधिकतम ₹{{limit}} अनुमत है।',
        withdrawalFailed: 'निकासी अनुरोध विफल। कृपया पुनः प्रयास करें या सहायता से संपर्क करें।',
        unauthenticated: 'जारी रखने के लिए कृपया फिर से लॉग इन करें।',
        invalidAmount: 'अमान्य निकासी राशि। कृपया जांच करें और पुनः प्रयास करें।',
        preconditionFailed: 'अनुरोध संसाधित नहीं किया जा सकता। कृपया अपना बैलेंस जांचें और पुनः प्रयास करें।',
        requestedAmount: 'अनुरोधित राशि:',
        serviceFee: 'सेवा शुल्क (5%):',
        youllReceiveLabel: 'आपको मिलेगा:',
        requestWithdrawal: 'निकासी अनुरोध',
        processing: 'प्रसंस्करण हो रहा है...',
        lifetimeWithdrawn: 'जीवनकाल निकासी',
        thisMonth: 'इस महीने',
        totalRequests: 'कुल अनुरोध',
        yourRequestsSummary: 'आपके अनुरोधों का सारांश',
        paid: 'भुगतान किया गया',
        pending: 'लंबित',
        rejected: 'अस्वीकृत',
        fullHistory: 'पूर्ण इतिहास',
        updateBank: 'बैंक अपडेट करें',
        comingSoon: 'जल्द आ रहा है',
        rewardFeaturesComing: 'हम रोमांचक रिवार्ड सुविधाओं पर काम कर रहे हैं। बने रहें!',
        goodToKnow: 'जानने योग्य',
        minimumPerWithdrawal: 'न्यूनतम: प्रति निकासी ₹100',
        processingTime: 'प्रसंस्करण: 2-6 कार्य दिवस',
        serviceFeeInfo: 'सेवा शुल्क: 5% (विवरण में दिखाया गया)',
        dailyLimit: 'दैनिक सीमा: प्रति दिन अधिकतम 2 अनुरोध | प्रति दिन अधिकतम ₹10,000',
        paymentRequest: 'भुगतान अनुरोध',
        withdrawalRequestSubmittedNotification: '₹{{amount}} के लिए आपका निकासी अनुरोध समीक्षा के लिए सबमिट किया गया है',
        ago: 'पहले',
        min: 'मिनट',
        mins: 'मिनट',
        hour: 'घंटा',
        hours: 'घंटे',
        day: 'दिन',
        days: 'दिन',
      },
      dashboard: {
        totalEarnings: 'कुल कमाई',
        loading: 'लोड हो रहा है...',
        availableBalance: 'उपलब्ध बैलेंस',
        memberSince: 'सदस्यता तिथि',
        bondhuId: 'BONDHU ID',
        activityOverview: 'गतिविधि अवलोकन',
        last3Months: 'पिछले 3 महीने',
        last6Months: 'पिछले 6 महीने',
        thisYear: 'पिछले 12 महीने',
        noActivityData: 'कोई गतिविधि डेटा उपलब्ध नहीं',
        earnings: 'कमाई',
        withdrawals: 'निकासी',
        whatsNew: 'नया क्या है',
        live: 'लाइव',
        schemes: 'योजनाएं',
        loadingSchemes: 'योजनाएं लोड हो रही हैं...',
        noSchemesAvailable: 'कोई योजना उपलब्ध नहीं',
        downloads: 'डाउनलोड',
        quickLinks: 'त्वरित लिंक',
        termsAndConditions: 'नियम और शर्तें',
        products: 'उत्पाद',
        exploreProducts: 'हमारे उत्पाद देखें',
        colourCombo: 'रंग कॉम्बो',
        colorCombinations: 'रंग संयोजन',
        contact: 'संपर्क',
        getInTouch: 'संपर्क करें',
        customerSupport: 'सहायता',
        weAreHereToHelp: 'हम मदद के लिए यहां हैं!',
        hello: 'नमस्ते! 👋',
        howCanWeHelp: 'आज हम आपकी कैसे मदद कर सकते हैं?',
        featuresComingSoon: 'जल्द आ रहा है',
        typeYourMessage: 'अपना संदेश टाइप करें...',
        demoModeMessage: 'डेमो मोड: संदेश भेजना जल्द ही लागू किया जाएगा!\n\nआपका संदेश: ',
        poweredBy: 'LilQoder Ai द्वारा संचालित',
        user: 'उपयोगकर्ता',
        painter: 'पेंटर',
        fileNotAvailable: 'फ़ाइल अभी तक उपलब्ध नहीं है। कृपया व्यवस्थापक से संपर्क करें।',
        failedToDownload: 'योजना डाउनलोड करने में विफल',
      },
      profileHero: {
        rank: 'रैंक',
        moreScansNeeded: '{{count}} और पॉइंट्स की आवश्यकता • {{next}} की ओर प्रगति',
        masterLevelAchieved: 'मास्टर स्तर प्राप्त! • आपने उच्चतम स्तर तक पहुंच गए हैं!',
        scans: 'स्कैन',
        points: 'पॉइंट्स',
        required: 'आवश्यक',
        shareButton: 'साझा करें',
        viewProfileButton: 'प्रोफ़ाइल देखें',
        level: {
          master: 'मास्टर',
          diamond: 'डायमंड',
          platinum: 'प्लैटिनम',
          gold: 'गोल्ड',
          silver: 'सिल्वर',
          bronze: 'ब्रॉन्ज़',
          brightenUp: 'ब्राइटन अप',
          locked: 'लॉक',
        },
      },
    },
  },
};

const savedLang = localStorage.getItem('bondhu_app_language');

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLang && resources[savedLang as keyof typeof resources] ? savedLang : 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;

